import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDimensionsVAPT as fetchDimensionsVAPTService,
  saveNewSubDimensionByDimension as saveNewSubDimensionService,
  fetchVAPTSubDimensionsByDimensionId as fetchVAPTSubDimensionsByDimensionIdService,
  fetchDimensionsByCategory as fetchDimensionsByCategoryService,
  saveNewDimension as saveNewDimensionService,
  updateDimensionById as updateDimensionService
} from "services/modules/vapt/data";
import { fetchCloudTypes as fetchCloudTypesService } from "services/modules/vapt/alert";

import { useFormik } from "formik";
import * as Yup from "yup";
import { useNotification } from "config/Notification";
import Breadcrumbs from "config/Breadcrumb";
import {
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  CardHeader,
} from "@mui/material";


const ConfigureDimensions = () => {
  // Notification
  const { openNotification } = useNotification();

  // Redux dispatch
  const dispatch = useDispatch();

  // State variables
  const [selectedDimensions, setSelectedDimensions] = useState([]);
  const [cloudtype, setCloudtype] = useState(false);
  const [subDimension, setsubDimension] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isStep1Editing, setIsStep1Editing] = useState(false);
  const [isStep2Editing, setIsStep2Editing] = useState(false);
  const [editControlId, setEditControlId] = useState(null);
  const [open, setOpen] = React.useState(false);
  const [openStep2, setOpenStep2] = React.useState(false);

  // Redux selectors
  const vaptDimensionsContainer = useSelector((state) => state.vaptData.vaptDimensions);
  const cloudTypesContainer = useSelector((state) => state.vaptAlert.cloudtypes);

  // Fetches VAPT Dimensions by category and cloud type
  const fetchDimensionsByCategoryCloudtype = async (category, cloudtype) => {
    try {
      let params = { category: category, cloudtype: cloudtype };
      const response = await fetchDimensionsByCategoryService({ params });
      setSelectedDimensions(response);
    } catch (error) {
      openNotification("Error while fetching data", "error");
    }
  };

  // Fetch dimensions data based on the selected dimension type
  const fetchAllDimensionsByCategory = async (categoryId) => {
    try {
      fetchDimensionsByCategoryCloudtype(categoryId, "");
      const filteredCategories = vaptDimensionsContainer.filter((category) => {
        return category._id === categoryId;
      });
      if (filteredCategories.length > 0) {
        if (filteredCategories[0].slug !== "cloud") {
          setCloudtype(false);
          formikStep1.setFieldValue('cloudtypes', "");
        }
        else {
          setCloudtype(true);
        }
      }
    } catch (error) {
      openNotification("Error while fetching data", "error");
    }
  };

  // Fetch controls based on the selected dimension
  const fetchSubDimensionsByDimensionId = async (dimensionId) => {
    try {
      const response = await fetchVAPTSubDimensionsByDimensionIdService({
        dimensionId,
      });
      setsubDimension(response.data || []);
    } catch (error) {
      openNotification("Error while fetching controls", "error");
    }
  };

  useEffect(() => {
    dispatch(fetchDimensionsVAPTService({}));
    dispatch(fetchCloudTypesService());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // This method handles to change the pages
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handles change in category in step 1
  const handleCategoryChange = (event) => {
    const categoryId = event.target.value;
    fetchAllDimensionsByCategory(categoryId);
    formikStep1.setFieldValue('category', categoryId);
  };

  // Handles change in cloud type in step 1
  const handleCloudTypeChange = (event) => {
    const cloudtypeId = event.target.value;
    fetchDimensionsByCategoryCloudtype(formikStep1.values.category, cloudtypeId);
    formikStep1.setFieldValue('cloudtypes', cloudtypeId);
  };

  // Handles change in category in step 2
  const handleCategoryChangeform2 = (event) => {
    const categoryId = event.target.value;
    fetchAllDimensionsByCategory(categoryId);
    formikStep2.setFieldValue('category', categoryId);
  };

  // Handles change in cloud type in step 2
  const handleCloudTypeChangeform2 = (event) => {
    const cloudtypeId = event.target.value;
    fetchDimensionsByCategoryCloudtype(formikStep2.values.category, cloudtypeId);
    formikStep2.setFieldValue('cloudtypes', cloudtypeId);
  };

  // This method handles to change the rows per page based on user selection
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // Generates Slug based on the below regex
  const generateSlug = (label) => {
    return label
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, "-");
  };

  // Validation schemas for each step
  const validationSchemaStep1 = Yup.object({
    label: Yup.string().required("Required"),
    category: Yup.string().required("Required"),
  });
  // Formik setup for step 1
  const formikStep1 = useFormik({
    initialValues: {
      category: "",
      cloudtypes: "",
      label: "",
      slug: "",
      description: "",
      weight: 0,
      status: true,
      validationSchema: {},
    },
    validationSchema: validationSchemaStep1,
    onSubmit: async (values) => {
      try {
        // Check if there's an ID to determine if it's an edit
        let slug = generateSlug(values.label);
        let body = {
          label: values.label,
          slug: values.slug ? values.slug : slug,
          description: values.description,
          weight: values.weight,
          status: values.status,
          validationSchema: values.validationSchema,
        };
        const categoryId = values.category;
        const cloudtypeId = values.cloudtypes;
        let response;
        if (isStep1Editing) {
          let dimensionId = values.id;
          response = await updateDimensionService(dimensionId, { body }); // Update API
          if (response.status === 200) {
            openNotification("Dimension updated successfully", "success");
            formikStep1.resetForm();
            formikStep1.setFieldValue('category', categoryId);
            if (cloudtype !== "") {
              fetchDimensionsByCategoryCloudtype(categoryId, cloudtypeId);
              formikStep1.setFieldValue('cloudtypes', cloudtypeId);
            }
            else {
              fetchAllDimensionsByCategory(categoryId);
            }
            setIsStep1Editing(false);
          }
        } else {
          const params = { category: categoryId, cloudtype: cloudtypeId };
          response = await saveNewDimensionService({ body, params });
          if (response.status === 201) {
            openNotification("Dimension saved successfully", "success");
            formikStep1.resetForm();
            if (cloudtype !== "") {
              fetchDimensionsByCategoryCloudtype(categoryId, cloudtypeId);
              formikStep1.setFieldValue('category', categoryId);
              formikStep1.setFieldValue('cloudtypes', cloudtypeId);
            }
            else {
              fetchAllDimensionsByCategory(categoryId);
              formikStep1.setFieldValue('category', categoryId);
            }
            setIsStep1Editing(false);
          }
        }
      } catch (error) {
        openNotification("Error while saving dimension", "error");
      }
    },
  });

  // Handles the edit in step1
  const handleStep1Edit = (dimension) => {
    setIsStep1Editing(true);
    const categoryId = formikStep1.values.category;
    const cloudtype = formikStep1.values.cloudtypes;
    formikStep1.setValues({
      id: dimension.id,
      label: dimension.label,
      slug: dimension.slug,
      description: dimension.description,
      weight: dimension.weight,
      status: dimension.status,
      category: categoryId,
      cloudtypes: cloudtype,
      validationSchema: dimension.validationSchema,
    });
    setActiveStep(0);
  };
  // Handles status change in step1
  const handleStatusChange = (event) => {
    const newStatus = event.target.value === "true";
    if (!newStatus) {
      if (Number(formikStep1.values.weight) === 0) {
        formikStep1.setFieldValue("status", false);
      } else {
        setOpen(true);
      }
    } else {
      formikStep1.setFieldValue("status", true);
    }
  };

  const handleConfirmInactive = () => {
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Handle Status change in step2
  const handleStep2StatusChange = (event) => {
    const newStatus = event.target.value === "true";
    if (!newStatus) {
      if (Number(formikStep2.values.weight) === 0) {
        formikStep2.setFieldValue("status", false);
      } else {
        setOpenStep2(true);
      }
    } else {
      formikStep2.setFieldValue("status", true);
    }
  };

  const handleStep2ConfirmInactive = () => {
    setOpenStep2(false);
  };

  const handleStep2Close = () => {
    setOpenStep2(false);
  };
  const validationSchemaStep2 = Yup.object({
    dimension: Yup.string().required("Required"),
    label: Yup.string().required("Required"),
  });
  const formikStep2 = useFormik({
    initialValues: {
      category: "",
      cloudtypes: "",
      dimension: "",
      label: "",
      slug: "",
      description: "",
      weight: 0,
      status: true,
      validationSchema: {},
    },
    validationSchema: validationSchemaStep2,
    onSubmit: async (values) => {
      try {
        let slug = generateSlug(values.label);
        let body = {
          label: values.label,
          slug: values.slug ? values.slug : slug,
          description: values.description,
          weight: values.weight,
          status: values.status,
          validationSchema: values.validationSchema,
        };
        const categoryId = values.category;
        const cloudtypeId = values.cloudtypes;

        if (isStep2Editing) {
          let dimension = editControlId;
          const response = await updateDimensionService(dimension, { body });
          if (response.status === 200) {
            openNotification("Sub-Dimension updated successfully", "success");
            fetchSubDimensionsByDimensionId(values.dimension);
            formikStep2.resetForm();
            formikStep2.setFieldValue("dimension", values.dimension);
            formikStep2.setFieldValue('category', categoryId);
            formikStep2.setFieldValue('cloudtypes', cloudtypeId);
            setIsStep2Editing(false);
            setEditControlId(null);
          }
        }
        else {
          const response = await saveNewSubDimensionService({
            dimension: values.dimension,
            body,
          });
          if (response.status === 201) {
            openNotification("Sub-Dimension saved successfully", "success");
            fetchSubDimensionsByDimensionId(values.dimension);
            formikStep2.resetForm();
            formikStep2.setFieldValue("dimension", values.dimension);
            formikStep2.setFieldValue('category', categoryId);
            formikStep2.setFieldValue('cloudtypes', cloudtypeId);
          }
        }
      } catch (error) {
        openNotification("Error while saving control", "error");
      }
    },
  });

  const handleStep2EditClick = (subdimension) => {
    setIsStep2Editing(true);
    setEditControlId(subdimension.id);
    formikStep2.setFieldValue("label", subdimension.label);
    formikStep2.setFieldValue("slug", subdimension.slug);
    formikStep2.setFieldValue("description", subdimension.description);
    formikStep2.setFieldValue("weight", subdimension.weight);
    formikStep2.setFieldValue(
      "validationSchema",
      subdimension.validationSchema
    );
    formikStep2.setFieldValue("status", subdimension.status);
    formikStep2.setFieldValue(
      "validationSchema",
      subdimension.validationSchema
    );
  };

  useEffect(() => {
    if (formikStep2.values.dimension) {
      fetchSubDimensionsByDimensionId(formikStep2.values.dimension);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikStep2.values.dimension]);

  const steps = ["Add Dimension", "Add Sub-Dimension"];

  const formContainerStyle = {
    marginBottom: "20px",
    display: "flex",
    justifyContent: "center",
  };

  const tableContainerStyle = {
    marginBottom: "20px",
  };

  const centerCardStyle = {
    textAlign: "center",
  };

  const dividerStyle = {
    margin: "20px 0",
  };

  return (
    <div className="main-cont">
      <Card>
        <CardHeader
          title={
            <React.Fragment>
              <div className="card-head">
                <div className="flex-head">
                  <label className="label">
                    <Breadcrumbs paths={['VAPT Configurator', 'Configure Dimensions']} />
                  </label>
                </div>
                <div>
                  <Button
                    variant="contained"
                    className="back font"
                    onClick={() => window.history.back()}
                  >
                    Back
                  </Button>
                </div>
              </div>
            </React.Fragment>
          }
        />
        <CardContent>
          <Divider style={dividerStyle} />
          {
            <>
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
              <Divider style={dividerStyle} />
              <CardContent style={centerCardStyle}>
                {activeStep === 0 && (
                  <div style={formContainerStyle}>
                    <Card className="row company-screen-bg col-md-10">
                      <CardContent>
                        <form onSubmit={formikStep1.handleSubmit}>
                          <div className="row">
                            <FormControl
                              className="col-md-6 px-2"
                              margin="normal"
                            >
                              <InputLabel>Category</InputLabel>
                              <Select
                                name="category"
                                value={formikStep1.values.category}
                                onChange={handleCategoryChange}
                                error={
                                  formikStep1.touched.category &&
                                  Boolean(formikStep1.errors.category)
                                }
                              >
                                {vaptDimensionsContainer &&
                                  vaptDimensionsContainer.map((category) => (
                                    <MenuItem
                                      key={category._id}
                                      value={category._id}
                                    >
                                      {category.label}
                                    </MenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                            {cloudtype && (<FormControl
                              className="col-md-6 px-2"
                              margin="normal"
                            >
                              <InputLabel>Cloud Types</InputLabel>
                              <Select
                                name="cloudtypes"
                                value={formikStep1.values.cloudtypes}
                                onChange={handleCloudTypeChange}
                                error={formikStep1.touched.cloudtypes && Boolean(formikStep1.errors.cloudtypes)}
                              >
                                {cloudTypesContainer &&
                                  cloudTypesContainer.map((category) => (
                                    <MenuItem
                                      key={category.id}
                                      value={category.id}
                                    >
                                      {category.label}
                                    </MenuItem>
                                  ))}
                              </Select>
                            </FormControl>)}
                          </div>
                          <div className="row">
                            <FormControl
                              className="col-md-6 px-2"
                              margin="normal"
                            >
                              <TextField
                                label="Name"
                                name="label"
                                onChange={formikStep1.handleChange}
                                value={formikStep1.values.label}
                                error={
                                  formikStep1.touched.label &&
                                  Boolean(formikStep1.errors.label)
                                }
                                helperText={
                                  formikStep1.touched.label &&
                                  formikStep1.errors.label
                                }
                              />
                            </FormControl>
                          </div>
                          <div className="row">
                            <div className="col-md-12 px-2">
                              <FormControl fullWidth margin="normal">
                                <TextField
                                  label="Description"
                                  name="description"
                                  onChange={formikStep1.handleChange}
                                  value={formikStep1.values.description}
                                  error={
                                    formikStep1.touched.description &&
                                    Boolean(formikStep1.errors.description)
                                  }
                                  helperText={
                                    formikStep1.touched.description &&
                                    formikStep1.errors.description
                                  }
                                />
                              </FormControl>
                            </div>
                            {isStep1Editing && (
                              <>
                                <div
                                  className="row col-md-12 px-2"
                                  style={{ alignItems: "center" }}
                                >
                                  <FormControl
                                    component="fieldset"
                                    className="d-flex"
                                    margin="normal"
                                  >
                                    <RadioGroup
                                      row
                                      aria-label="status"
                                      name="status"
                                      value={
                                        formikStep1.values.status
                                          ? "true"
                                          : "false"
                                      }
                                      onChange={handleStatusChange}
                                    >
                                      <FormControlLabel
                                        value="true"
                                        control={<Radio />}
                                        label="Active"
                                      />
                                      <FormControlLabel
                                        value="false"
                                        control={<Radio />}
                                        label="Inactive"
                                      />
                                    </RadioGroup>
                                  </FormControl>
                                </div>
                              </>
                            )}
                          </div>

                          <div className="d-flex justify-content-center align-items-center"

                          >
                            <Button
                              color="primary"
                              variant="contained"
                              type="submit"
                            >
                              {isStep1Editing ? "Update" : "Save"}
                            </Button>
                            <Button
                              variant="contained"
                              className="danger mx-3"
                              onClick={() => {
                                formikStep1.resetForm();
                                setIsStep1Editing(false);
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              onClick={() => setActiveStep((prev) => prev + 1)}
                            >
                              Next
                            </Button>
                          </div>
                          <Dialog
                            open={open}
                            onClose={handleClose}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                          >
                            <DialogTitle id="alert-dialog-title">
                              {"Confirm Inactive Status"}
                            </DialogTitle>
                            <DialogContent>
                              <DialogContentText id="alert-dialog-description">
                                Would you really like to change the status to
                                "Inactive"? the weightage should be zero.
                              </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                              <Button
                                onClick={handleConfirmInactive}
                                color="secondary"
                                autoFocus
                              >
                                Ok
                              </Button>
                            </DialogActions>
                          </Dialog>
                        </form>
                      </CardContent>
                    </Card>
                  </div>
                )}
                {activeStep === 0 && selectedDimensions.length > 0 && (
                  <div style={tableContainerStyle}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Label</TableCell>
                          <TableCell>Weightage</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedDimensions
                          .slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage
                          )
                          .map((dimension, index) => (
                            <TableRow key={index}>
                              <TableCell>{dimension.label}</TableCell>
                              <TableCell>{dimension.weight}</TableCell>
                              <TableCell
                                style={{
                                  maxWidth: "200px",
                                  wordWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {dimension.description}
                              </TableCell>
                              <TableCell>
                                {dimension.status ? "Active" : "Inactive"}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  onClick={() => handleStep1Edit(dimension)}
                                >
                                  Edit
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>

                    <TablePagination
                      rowsPerPageOptions={[10, 25, 100]}
                      component="div"
                      count={selectedDimensions.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </div>
                )}

                {activeStep === 1 && (
                  <div style={formContainerStyle}>
                    <Card className="row company-screen-bg col-md-10">
                      <CardContent>
                        <form onSubmit={formikStep2.handleSubmit}>
                          <div className="row">
                            <FormControl
                              className="col-md-6 px-2"
                              margin="normal"
                            >
                              <InputLabel>Category</InputLabel>
                              <Select
                                name="category"
                                value={formikStep2.values.category}
                                onChange={handleCategoryChangeform2}
                                error={
                                  formikStep2.touched.category &&
                                  Boolean(formikStep2.errors.category)
                                }
                              >
                                {vaptDimensionsContainer &&
                                  vaptDimensionsContainer.map((category) => (
                                    <MenuItem
                                      key={category._id}
                                      value={category._id}
                                    >
                                      {category.label}
                                    </MenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                            {cloudtype && (<FormControl
                              className="col-md-6 px-2"
                              margin="normal"
                            >
                              <InputLabel>Cloud Types</InputLabel>
                              <Select
                                name="cloudtypes"
                                value={formikStep2.values.cloudtypes}
                                onChange={handleCloudTypeChangeform2}
                                error={formikStep2.touched.cloudtypes && Boolean(formikStep2.errors.cloudtypes)}
                              >
                                {cloudTypesContainer &&
                                  cloudTypesContainer.map((category) => (
                                    <MenuItem
                                      key={category.id}
                                      value={category.id}
                                    >
                                      {category.label}
                                    </MenuItem>
                                  ))}
                              </Select>
                            </FormControl>)}
                          </div>
                          <div className="row ">
                            <FormControl
                              className="col-md-6 px-2"
                              margin="normal"
                            >
                              <InputLabel>Dimension</InputLabel>
                              <Select
                                name="dimension"
                                value={formikStep2.values.dimension}
                                onChange={formikStep2.handleChange}
                                error={
                                  formikStep2.touched.dimension &&
                                  Boolean(formikStep2.errors.dimension)
                                }
                              >
                                {selectedDimensions &&
                                  selectedDimensions
                                    .filter(
                                      (dimension) => dimension.status === true
                                    )
                                    .map((dimension) => (
                                      <MenuItem
                                        key={dimension.id}
                                        value={dimension.id}
                                      >
                                        {dimension.label}
                                      </MenuItem>
                                    ))}
                              </Select>
                              {formikStep2.touched.dimension &&
                                formikStep2.errors.dimension && (
                                  <div className="error-message">
                                    {formikStep2.errors.dimension}
                                  </div>
                                )}
                            </FormControl>
                            <FormControl
                              className="col-md-6 px-2"
                              margin="normal"
                            >
                              <TextField
                                label="Name"
                                name="label"
                                onChange={formikStep2.handleChange}
                                value={formikStep2.values.label}
                                error={
                                  formikStep2.touched.label &&
                                  Boolean(formikStep2.errors.label)
                                }
                                helperText={
                                  formikStep2.touched.label &&
                                  formikStep2.errors.label
                                }
                              />
                            </FormControl>
                          </div>

                          <FormControl fullWidth margin="normal">
                            <TextField
                              label="Description"
                              name="description"
                              onChange={formikStep2.handleChange}
                              value={formikStep2.values.description}
                              error={
                                formikStep2.touched.description &&
                                Boolean(formikStep2.errors.description)
                              }
                              helperText={
                                formikStep2.touched.description &&
                                formikStep2.errors.description
                              }
                            />
                          </FormControl>
                          {isStep2Editing && (
                            <>
                              <div
                                className="row col-md-12 px-2"
                                style={{ alignItems: "center" }}
                              >
                                <FormControl
                                  component="fieldset"
                                  className="d-flex"
                                  margin="normal"
                                >
                                  <RadioGroup
                                    row
                                    aria-label="status"
                                    name="status"
                                    value={
                                      formikStep2.values.status
                                        ? "true"
                                        : "false"
                                    }
                                    onChange={handleStep2StatusChange}
                                  >
                                    <FormControlLabel
                                      value="true"
                                      control={<Radio />}
                                      label="Active"
                                    />
                                    <FormControlLabel
                                      value="false"
                                      control={<Radio />}
                                      label="Inactive"
                                    />
                                  </RadioGroup>
                                </FormControl>
                              </div>
                            </>
                          )}

                          <div className="d-flex justify-content-center align-items-center" >
                            <Button
                              variant="contained"
                              onClick={() => setActiveStep((prev) => prev - 1)}
                            >
                              Back
                            </Button>
                            <Button
                              color="primary"
                              variant="contained"
                              type="submit"
                              className="mx-3"
                            >
                              {isStep2Editing ? "Update" : "Save"}
                            </Button>
                            <Button
                              variant="contained"
                              className="danger"
                              onClick={() => {
                                let dimension = formikStep2.values.dimension;
                                formikStep2.resetForm();
                                formikStep2.setFieldValue(
                                  "dimension",
                                  dimension
                                );
                                setIsStep2Editing(false);
                                setEditControlId(null);
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                          <Dialog
                            open={openStep2}
                            onClose={handleStep2Close}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                          >
                            <DialogTitle id="alert-dialog-title">
                              {"Confirm Inactive Status"}
                            </DialogTitle>
                            <DialogContent>
                              <DialogContentText id="alert-dialog-description">
                                Would you really like to change the status to
                                "Inactive"? the weightage should be zero.
                              </DialogContentText>
                            </DialogContent>
                            <DialogActions>

                              <Button
                                onClick={handleStep2ConfirmInactive}
                                color="secondary"
                                autoFocus
                              >
                                Ok
                              </Button>
                            </DialogActions>
                          </Dialog>
                        </form>
                      </CardContent>
                    </Card>
                  </div>
                )}
                {activeStep === 1 && subDimension.length > 0 && (
                  <div style={tableContainerStyle}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Label</TableCell>
                          <TableCell>Weightage</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {subDimension.map((dimension, index) => (
                          <TableRow key={index}>
                            <TableCell>{dimension.label}</TableCell>
                            <TableCell>{dimension.weight}</TableCell>
                            <TableCell
                              style={{
                                maxWidth: "200px",
                                wordWrap: "break-word",
                                whiteSpace: "normal",
                              }}
                            >
                              {dimension.description}
                            </TableCell>
                            <TableCell>
                              {dimension.status ? "Active" : "Inactive"}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => handleStep2EditClick(dimension)}
                              >
                                Edit
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <TablePagination
                      rowsPerPageOptions={[10, 25, 100]}
                      component="div"
                      count={subDimension.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </div>
                )}

                <Divider style={dividerStyle} />
              </CardContent>
            </>
          }
        </CardContent>
      </Card>
    </div>
  );
};
export default ConfigureDimensions;
